<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import StepIndicator from '@/components/marketplace/company/registration/StepIndicator.vue'
import GetStartedStep from '@/components/marketplace/company/registration/GetStartedStep.vue'
import CompanyDetailsStep from '@/components/marketplace/company/registration/CompanyDetailsStep.vue'
import CompanyProfileStep from '@/components/marketplace/company/registration/CompanyProfileStep.vue'
import OperationsOverviewStep from '@/components/marketplace/company/registration/OperationsOverviewStep.vue'
import ManagerInformationStep from '@/components/marketplace/company/registration/ManagerInformationStep.vue'

import { useCompanyRegistrationStore } from '@/stores/companyRegistrationStore'
import { useCompanyIndustryProfileStore } from '@/stores/companyIndustryProfileStore'

const store = useCompanyRegistrationStore()
const industryProfileStore = useCompanyIndustryProfileStore()

const stepLabels = [
  'Get Started',
  'Company Details',
  'Company Profile',
  'Operations Overview',
  'Manager Info',
]

watch(() => store.currentStep, () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
})

onMounted( async() => {
  await store.initDraft()
  store.fetchIndustries()
  store.fetchCompanyIndustryOptions()
  industryProfileStore.fetchProfiles()
})
</script>

<template>
  <div v-if="industryProfileStore.error" class="py-12 px-4 text-center text-sm text-red-600">
    {{ industryProfileStore.error }}
  </div>
  <div v-else-if="store.isLoaded && industryProfileStore.isLoaded" class="min-h-screen bg-background py-12 px-4">
    <div class="mx-auto max-w-4xl">
      <div class="mb-8 text-center">
        <h1 class="text-3xl font-semibold mb-2">Company Registration</h1>
        <p class="text-muted-foreground">
          Complete your company profile to get started with Screened
        </p>
      </div>

      <!-- Step indicator -->
      <StepIndicator
        :current-step="store.currentStep"
        :total-steps="store.totalSteps"
        :step-labels="stepLabels"
      />


      <!-- Step 1: Get started step -->
      <GetStartedStep
        v-if="store.currentStep === 0"
        :data="store.form"
        :isSaving="store.isSaving"
        @update="store.updateForm"
        @next="store.nextStep"
      />

      <!-- Step 2: Company details step-->
      <CompanyDetailsStep
        v-if="store.currentStep === 1"
        :draftId="store.draftId"
        :industries="store.industries || []"
        :company-industry-options="store.companyIndustryOptions"
        @next="store.nextStep"
        @back="store.prevStep"
      />

      <!-- Step 3: Company Profile -->
      <CompanyProfileStep
        v-if="store.currentStep === 2"
        :data="store.form"
        :industries="store.industries || []"
        @update="store.updateForm"
        @next="store.nextStep"
        @back="store.prevStep"
      />

      <!-- Step 4: Operations Overview -->
      <OperationsOverviewStep
        v-if="store.currentStep === 3"
        :data="store.form"
        @update="store.updateForm"
        @next="store.nextStep"
        @back="store.prevStep"
      />

      <!-- Step 5: Manager Information -->
      <ManagerInformationStep
        v-if="store.currentStep === 4"
        :data="store.form"
        :isSaving="store.isSaving"
        @update="store.updateForm"
        @submit="store.submit"
        @next="store.nextStep"
        @back="store.prevStep"
      />
    </div>
  </div>
</template>
